import { h, Component } from 'preact';
import { observer } from 'mobx-preact';
import { Text } from 'preact-i18n';
import { route } from 'preact-router';

function isoDateOnly(value) {
    if (!value) {
        return '';
    }
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
        return '';
    }
    const month = `${date.getMonth() + 1}`.padStart(2, '0');
    const day = `${date.getDate()}`.padStart(2, '0');
    return `${date.getFullYear()}-${month}-${day}`;
}

function addOneYear(value) {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
        return new Date();
    }
    date.setFullYear(date.getFullYear() + 1);
    return date;
}

function bumpYearInTitle(title = '', newYear) {
    if (!newYear) {
        return title;
    }
    return title.replace(/\b(19|20)\d{2}\b/, `${newYear}`);
}

@observer
class RaceDuplicate extends Component {
    constructor(props) {
        super(props);
        const { race = {} } = props;
        const newStart = race.startDate ? addOneYear(race.startDate) : new Date();
        this.state = {
            startDate: isoDateOnly(newStart),
            title: bumpYearInTitle(race.title, newStart.getFullYear()),
            titleTouched: false,
            saving: false,
            hasError: false,
            classErrors: 0,
            newRaceId: null,
        };
    }

    onDateChange = e => {
        const startDate = e.target.value;
        this.setState(prevState => {
            const nextState = { startDate };
            if (!prevState.titleTouched) {
                const { race = {} } = this.props;
                const year = startDate ? new Date(startDate).getFullYear() : null;
                nextState.title = bumpYearInTitle(race.title, year);
            }
            return nextState;
        });
    }

    onTitleChange = e => {
        this.setState({ title: e.target.value, titleTouched: true });
    }

    duplicateRace = async () => {
        const { raceId, callback = () => {} } = this.props;
        const { raceStore } = this.props.stores;
        const { startDate, title, saving } = this.state;
        if (saving || !startDate || !title) {
            return;
        }
        this.setState({ saving: true, hasError: false });
        const result = await raceStore.duplicateRace(raceId, { startDate, title });
        if (result?.race?.id) {
            if (result.raceClassErrors > 0) {
                this.setState({
                    saving: false,
                    classErrors: result.raceClassErrors,
                    newRaceId: result.race.id,
                });
                return;
            }
            callback();
            route(`/race/${result.race.id}/admin/edit`);
        } else {
            this.setState({ saving: false, hasError: true });
        }
    }

    goToNewRace = () => {
        const { callback = () => {} } = this.props;
        const { newRaceId } = this.state;
        callback();
        route(`/race/${newRaceId}/admin/edit`);
    }

    render() {
        const { callback = () => {} } = this.props;
        const { startDate, title, saving, hasError, classErrors } = this.state;

        return (<>
            <div class='w-100 d-flex justify-content-center'>
                <div class='w-100' style='max-width: 960px; padding-bottom: 120px;'>
                    <div class='px-4 pt-4'>
                        <h5>
                            <i class='fa-solid fa-copy me-2' />
                            <Text id='race.duplicate-title'>Copy race to new dates</Text>
                        </h5>

                        <div class='alert alert-info mt-3'>
                            <Text id='race.duplicate-info'>
                                The race and all raceclasses (incl. checkpoints), crew and payment
                                setup will be copied. Contestants, results, statistics and comments
                                are not copied. All dates are shifted to match the new start date.
                                The copy is hidden from the public race list until you publish it.
                            </Text>
                        </div>

                        <div class='mb-3'>
                            <label class='form-label' for='raceDuplicateStartDate'>
                                <Text id='race.duplicate-start-date'>New start date</Text>
                            </label>
                            <input
                                id='raceDuplicateStartDate'
                                type='date'
                                class='form-control'
                                value={startDate}
                                onInput={this.onDateChange}
                            />
                        </div>

                        <div class='mb-3'>
                            <label class='form-label' for='raceDuplicateTitle'>
                                <Text id='race.duplicate-new-title'>Title of new race</Text>
                            </label>
                            <input
                                id='raceDuplicateTitle'
                                type='text'
                                class='form-control'
                                value={title}
                                onInput={this.onTitleChange}
                            />
                        </div>

                        {hasError && <div class='alert alert-danger'>
                            <Text id='race.duplicate-error'>Could not copy the race. Please try again.</Text>
                        </div>}

                        {classErrors > 0 && <div class='alert alert-warning'>
                            <Text id='race.duplicate-class-errors'>
                                The race was copied, but some raceclasses could not be copied.
                                Check the new race and add the missing classes manually.
                            </Text>
                        </div>}
                    </div>

                    <div class='fixed-bottom d-flex justify-content-center py-3 bg-body border-top'>
                        {classErrors > 0 ? <button
                            type='button'
                            class='btn btn-primary rounded-pill'
                            onClick={this.goToNewRace}
                        >
                            <i class='fa-solid fa-arrow-right me-2' />
                            <Text id='race.duplicate-continue'>Go to new race</Text>
                        </button> : <>
                            <button
                                type='button'
                                class='btn btn-secondary rounded-pill me-3'
                                onClick={() => callback()}
                            >
                                <Text id='race.duplicate-cancel'>Cancel</Text>
                            </button>
                            <button
                                type='button'
                                class='btn btn-primary rounded-pill'
                                disabled={saving || !startDate || !title}
                                onClick={this.duplicateRace}
                            >
                                {saving ? <>
                                    <span class='spinner-border spinner-border-sm me-2' role='status' aria-hidden='true' />
                                    <Text id='race.duplicate-saving'>Copying...</Text>
                                </> : <>
                                    <i class='fa-solid fa-copy me-2' />
                                    <Text id='race.duplicate'>Copy race</Text>
                                </>}
                            </button>
                        </>}
                    </div>
                </div>
            </div>
        </>);
    }
}

export default RaceDuplicate;
